import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService, MsalGuardConfiguration, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { filter } from 'rxjs';
import { GraphService } from './graph.service';
import { environment } from '../../../environments/environment';

function parseRoles(claims: Record<string, unknown>): string[] {
  const raw = claims['roles'] ?? claims['role'] ?? [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') return [raw];
  return [];
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private msal = inject(MsalService);
  private msalBroadcast = inject(MsalBroadcastService);
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private destroyRef = inject(DestroyRef);
  private graphService = inject(GraphService);
  private router = inject(Router);

  name = signal('');
  email = signal('');
  roles = signal<string[]>([]);
  avatarSrc = signal<string | undefined>(undefined);

  role = computed(() => this.roles()[0] ?? '');
  isAdmin = computed(() => this.roles().includes('admin'));
  isReadonly = computed(() => !this.isAdmin());
  initials = computed(() => {
    const parts = this.name().split(' ').filter(Boolean);
    if (parts.length === 0) return '';
    return parts.length === 1
      ? parts[0].slice(0, 2).toUpperCase()
      : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  });

  loginDisplay = false;

  load(): void {
    this.msal.handleRedirectObservable().subscribe();

    this.msalBroadcast.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_SUCCESS
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.msal.instance.getAllAccounts().length === 0) {
          void this.router.navigateByUrl('/');
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcast.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay(): void {
    this.loginDisplay = this.msal.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msal.instance.getActiveAccount();

    if (!activeAccount && this.msal.instance.getAllAccounts().length > 0) {
      let accounts = this.msal.instance.getAllAccounts();
      activeAccount = accounts[0];
      this.msal.instance.setActiveAccount(activeAccount);
    }

    if (activeAccount) {
      const claims = (activeAccount.idTokenClaims as Record<string, unknown>) ?? {};
      this.name.set((claims["name"] ?? claims["preferred_username"] ?? claims["upn"] ?? "") as string);
      this.email.set((claims["email"] ?? claims["preferred_username"] ?? claims["upn"] ?? "") as string);

      this.msal.acquireTokenSilent({
        scopes: environment.api.scopes,
        account: activeAccount
      }).subscribe(result => {
        const payload = JSON.parse(atob(result.accessToken.split(".")[1]));
        this.roles.set(parseRoles(payload));
      });

      if (!this.avatarSrc()) {
        this.graphService.getPhotoUrl().subscribe({
          next: url => {
            this.avatarSrc.set(url);
          },
          error: err => console.error("photo fetch error:", err),
        });
      }
    }
  }

  loginRedirect(redirectStartPage?: string) {
    if (this.msalGuardConfig.authRequest) {
      this.msal.loginRedirect({ ...this.msalGuardConfig.authRequest, redirectStartPage: redirectStartPage } as RedirectRequest);
    } else {
      this.msal.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.msal
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.msal.instance.setActiveAccount(response.account);
        });
    } else {
      this.msal
        .loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.msal.instance.setActiveAccount(response.account);
        });
    }
  }

  logout() {
    this.msal.logoutRedirect();
  }
}
